import CustomTooltip from '../../../../shared/CustomTooltip';
import { Add, Delete } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Button,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { formatCurrency } from '../../../../../shared/utils';
import { CleaningFeeType, FeeApplies } from '../../../../enums/experience';
import FormSelect from '../../../../shared/FormSelect';
import FormTextField from '../../../../shared/FormTextField';
import { Experience } from '../../../../types/experience';

interface Props {
    availableZones: any[];
    setTemporaryFees: (data: any) => void;
    temporaryFees: any;
    experience: Experience;
}

const CleaningFeeForm = ({
    availableZones,
    setTemporaryFees,
    temporaryFees,
    experience,
}: Props) => {
    const methods = useFormContext();
    const {
        control,
        reset,
        formState: { errors },
    } = methods;
    const cleaningFeeType = useWatch({ control, name: 'cleaningFeeType' });
    const hasCombinedRooms =
        experience?.linkedModules.length > 0 &&
        experience?.linkType === 'combine';
    const [showByZoneWarning, setShowByZoneWarning] = useState(false);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'tiers',
    });

    const theme = useTheme();

    const addTier = () => {
        append({ rooms: 1, price: undefined });
    };

    const removeTierAt = (index: number) => remove(index);

    const handleFlexibleTypeChange = (e: any) => {
        const cleaningFeeType = e?.target?.value;
        const prevCleaningFeeType = methods.getValues('cleaningFeeType');

        const cannotSetByZone =
            hasCombinedRooms && cleaningFeeType === CleaningFeeType.ByZone;
        if (cannotSetByZone) {
            setShowByZoneWarning(true);
            if (e?.target) e.target.value = prevCleaningFeeType;
            return;
        }

        if (showByZoneWarning) setShowByZoneWarning(false);

        const currentValues = methods.watch();
        const obj = { [currentValues.cleaningFeeType]: currentValues };
        setTemporaryFees({ ...temporaryFees, ...obj });

        const details = temporaryFees[cleaningFeeType];

        if (cleaningFeeType === CleaningFeeType.Flat) {
            reset({
                chargeType: 'cleaningFee',
                cleaningFeeType: CleaningFeeType.Flat,
                price: null,
                ...(details ? details : {}),
            });
        }
        if (cleaningFeeType === CleaningFeeType.PerRoom) {
            reset({
                chargeType: 'cleaningFee',
                cleaningFeeType: CleaningFeeType.PerRoom,
                basePrice: null,
                unitPrice: null,
                applies: FeeApplies.PerBooking,
                guests: 1,
                ...(details ? details : {}),
            });
        }
        if (cleaningFeeType === CleaningFeeType.Tiered) {
            reset({
                chargeType: 'cleaningFee',
                cleaningFeeType: CleaningFeeType.Tiered,
                basePrice: null,
                tiers: [],
                applies: FeeApplies.PerBooking,
                guests: 1,
                ...(details ? details : {}),
            });
        }
        if (cleaningFeeType === CleaningFeeType.ByZone) {
            reset({
                chargeType: 'cleaningFee',
                cleaningFeeType: CleaningFeeType.ByZone,
                basePrice: null,
                applies: FeeApplies.PerBooking,
                spaces: availableZones,
                guests: 1,
                ...(details ? details : {}),
            });
        }
    };

    return (
        <Paper variant='outlined' sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Grid
                        sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
                    >
                        <Typography variant='h6' fontWeight='bold'>
                            Flexible Type
                        </Typography>
                        <CustomTooltip
                            sx={{
                                display: 'flex',
                            }}
                            title={
                                <Grid>
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                Minimum fee:
                                            </span>{' '}
                                            A minimum cleaning fee applies to
                                            every booking, with any flexible
                                            rates charged in addition to this
                                            base amount.
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                Fee Per room:
                                            </span>{' '}
                                            A fixed cleaning fee is applied to
                                            each room booked, in addition to the
                                            minimum cleaning amount.
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                Tiered:
                                            </span>{' '}
                                            Set price tiers based on the number
                                            of rooms. The tier that corresponds
                                            to the booking’s room count will be
                                            added on top of the minimum cleaning
                                            fee.
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                By Zone:
                                            </span>{' '}
                                            Assign a cleaning fee to each zone
                                            or space. The fees for all booked
                                            zones are added to the minimum
                                            cleaning fee.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Grid>

                    <Typography variant='body3'>
                        Set how you want to charge for cleaning
                    </Typography>
                </Box>
                <FormSelect
                    name='cleaningFeeType'
                    control={control}
                    label='Select Type'
                    onChange={handleFlexibleTypeChange}
                    options={[
                        {
                            value: CleaningFeeType.PerRoom,
                            label: 'Per Room',
                        },
                        {
                            value: CleaningFeeType.Tiered,
                            label: 'Tiered',
                        },
                        {
                            value: CleaningFeeType.ByZone,
                            label: 'By Zone',
                        },
                    ]}
                />
                {showByZoneWarning && (
                    <Alert sx={{ mt: 1 }} severity='warning'>
                        By Zone cleaning fees cannot be set for combined
                        experiences. Unlink the properties or select a different
                        cleaning fee type.
                    </Alert>
                )}
            </Box>

            {cleaningFeeType === CleaningFeeType.PerRoom && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='h6' fontWeight='bold'>
                            Per Room Configuration
                        </Typography>

                        <Typography variant='body3'>
                            Set the minimum fee and fee per room
                        </Typography>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormTextField
                                name='basePrice'
                                label='Minimum Fee'
                                type='number'
                                control={control}
                                helperText='General cleaning of the venue (toilets, kitchen, hallway)'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Typography>$</Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField
                                name='unitPrice'
                                label='Fee per Room'
                                type='number'
                                control={control}
                                helperText='Per office, per meeting room'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Typography>$</Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            {cleaningFeeType === CleaningFeeType.Tiered && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Grid
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant='h6' fontWeight='bold'>
                                Tiers Configuration
                            </Typography>
                            <CustomTooltip
                                sx={{
                                    display: 'flex',
                                }}
                                title={
                                    <Grid>
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                flexDirection: 'column',
                                                width: '250px',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    paddingBottom: '5px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                Example:
                                            </Typography>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    Minimum Fee
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(500)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    Tier A: 1-2 rooms
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(200)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    Tier B: 3-5 rooms
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(300)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    There is a reservation for 3
                                                    people for an experience
                                                    with a base price of $2000.
                                                    It will fall in Tier B.
                                                    Therefore, the calculation
                                                    will be like this:
                                                </Typography>
                                            </Grid>

                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    {'Base Price '}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(2000)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    {'Minimum cleaning Fee'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(500)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    Applied Tier (3-5 Rooms)
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(300)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                sx={{
                                                    mt: 1,
                                                    pt: 1,
                                                    borderTop: `1px solid ${theme.palette.faded.main}`,
                                                }}
                                            >
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            paddingTop: '2px',
                                                        }}
                                                    >
                                                        GST 10%
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatCurrency(280)}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            paddingTop: '2px',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {'Total'}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            textAlign: 'right',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {formatCurrency(3080)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        </Grid>

                        <Typography variant='body3'>
                            Set the minimum fee and add at least two tiers
                        </Typography>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <FormTextField
                                name='basePrice'
                                label='Minimum Fee'
                                type='number'
                                control={control}
                                helperText='General cleaning fee'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Typography>$</Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            variant='outlined'
                            startIcon={<Add />}
                            onClick={addTier}
                            size='small'
                        >
                            Add Tier
                        </Button>
                    </Box>

                    {errors?.tiers && (
                        <FormHelperText error sx={{ mb: 2 }}>
                            {(errors.tiers as any)?.message ||
                                'Add at least two tiers'}
                        </FormHelperText>
                    )}

                    {fields.length > 0 && (
                        <TableContainer component={Paper} variant='outlined'>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Rooms</TableCell>
                                        <TableCell>Fee ($)</TableCell>
                                        <TableCell width={50}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fields.map((field, index) => (
                                        <TableRow
                                            key={
                                                field.id /* RHF's internal field id */
                                            }
                                        >
                                            <TableCell>
                                                <FormTextField
                                                    name={`tiers.${index}.rooms`}
                                                    type='number'
                                                    size='small'
                                                    control={control}
                                                    inputProps={{
                                                        'aria-label': `Tier ${index + 1} rooms`,
                                                        min: 1,
                                                    }}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormTextField
                                                    name={`tiers.${index}.price`}
                                                    type='number'
                                                    size='small'
                                                    control={control}
                                                    hideError
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position='start'>
                                                                <Typography>
                                                                    $
                                                                </Typography>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    inputProps={{
                                                        'aria-label': `Tier ${index + 1} price`,
                                                        min: 0,
                                                    }}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={() =>
                                                        removeTierAt(index)
                                                    }
                                                    size='small'
                                                    color='error'
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {cleaningFeeType === CleaningFeeType.ByZone && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='h6' fontWeight='bold'>
                            Zones Configuration
                        </Typography>
                        <Typography variant='body3'>
                            Set the minimum fee and the fee for at least one
                            zone
                        </Typography>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <FormTextField
                                name='basePrice'
                                label='Minimum Fee'
                                type='number'
                                control={control}
                                helperText='General cleaning fee'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Typography>$</Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant='subtitle2' sx={{ mb: 2 }}>
                        Zone Fees
                    </Typography>

                    {errors?.zones && (
                        <FormHelperText error sx={{ mb: 2 }}>
                            {(errors.zones as any)?.message ||
                                'At least one zone must have a cleaning fee'}
                        </FormHelperText>
                    )}

                    {availableZones.length > 0 ? (
                        <Grid container spacing={2}>
                            {availableZones.map((zone, i) => (
                                <Grid item xs={12} sm={6} key={zone.id}>
                                    <FormTextField
                                        name={`spaces.${i}.cleaningFee`}
                                        label={zone.assignedName || zone.name}
                                        type='number'
                                        control={control}
                                        hideError
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Typography>$</Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Alert severity='info'>
                            Add spaces to your experience to configure
                            zone-based cleaning fees.
                        </Alert>
                    )}
                </Box>
            )}

            {cleaningFeeType !== CleaningFeeType.Flat && (
                <Box sx={{ mb: 3 }}>
                    <Grid
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography variant='subtitle2'>
                            Set whether the fee applies per booking or per night
                        </Typography>

                        <CustomTooltip
                            sx={{
                                display: 'flex',
                            }}
                            title={
                                <Grid>
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                Per Booking:
                                            </span>{' '}
                                            Charged once for the entire stay,
                                            regardless of the number of
                                            nights.{' '}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            <span
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                Per Night:
                                            </span>{' '}
                                            Charged for each night of the stay,
                                            added to the total nightly cost.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormSelect
                                name='applies'
                                control={control}
                                label='Applies'
                                options={[
                                    {
                                        value: FeeApplies.PerBooking,
                                        label: 'Per booking',
                                    },
                                    {
                                        value: FeeApplies.PerNight,
                                        label: 'Per night',
                                    },
                                ]}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Paper>
    );
};

export default CleaningFeeForm;
